// External
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ButtonStyled } from '@nike/nike-design-system-components';
import { bool, func, string } from 'prop-types';
import join from 'url-join';

// Internal
import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { globalHeaders } from '~APP_UTIL/http';
import FileInput from '../../../../components/formInputs/FileInput';
import FilesDisplay from '../filesDisplay/FilesDisplay';
import { normalizeResponseHeaders } from '../actions';
import Modal from '../../../../modal/Modal';

const api2 = join(import.meta.env.VITE_API2_URL, 'v1');

export const UploadCatalogModal = ({ isOpen, closeModal, classname }) => {
  const [uploadedFiles, updatedUploadedFiles] = useState([]);
  const [currentFiles, updateCurrentFiles] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { refreshTableData } = useContext(TableServerSideContext);

  const fileInputId = `${classname}Input`;

  useEffect(() => {
    return updatedUploadedFiles([]);
  }, [updatedUploadedFiles]);

  const handleFilesChange = useCallback(files => {
    if (Array.isArray(files)) {
      updateCurrentFiles(files);
      setInvalidRows(prev => (prev.length > 0 ? [] : prev));
    }
  }, []);

  const title = 'Upload Catalog Headers';

  const content = () => {
    return (
      <div className={`${classname}__input`}>
        <FileInput
          id={fileInputId}
          name={fileInputId}
          multiple
          allowDuplicates={false}
          disabled={isSubmitting || uploadedFiles?.length > 0}
          accept='.xlsx,.xml'
          onChange={handleFilesChange}
          FileSelectLabel='Select Files'
          fileDisplayComponent={props => (
            <FilesDisplay {...props} uploadedFiles={uploadedFiles} isSubmitting={isSubmitting} />
          )}
        />
        {invalidRows.length > 0 && (
          <div className={`${classname}__errors`}>
            <span className={`${classname}__errors__title`}>The following rows have errors:</span>
            <div className={`${classname}__errors__list`}>
              {invalidRows.map(({ rowNumber, catalogName, errors }) => (
                <div key={rowNumber} className={`${classname}__errors__row`}>
                  <strong>Row {rowNumber}</strong>
                  {catalogName && catalogName !== '(empty)' ? ` — ${catalogName}` : ''}
                  <div className={`${classname}__errors__fields`}>
                    {errors.map(({ field, message }) => (
                      <span key={field} className={`${classname}__errors__field`}>{message}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const footer = () => {
    const submit = async () => {
      setIsSubmitting(true);
      setInvalidRows([]);
      const formData = new FormData();
      for (let i = 0; i < currentFiles.length; i++) {
        formData.append('catalogs', currentFiles[i]);
      }
      try {
        const response = await fetch(join(api2, '/catalogs/createFromExcel'), {
          method: 'POST',
          headers: { ...globalHeaders },
          body: formData
        });
        const data = await response.json();
        if (!response.ok) {
          const rows = data?.invalidRows || [];
          if (rows.length > 0) {
            setInvalidRows(rows);
          } else {
            setInvalidRows([{
              rowNumber: 0,
              errors: [{ field: 'server', message: `Upload failed with status ${response.status}` }]
            }]);
          }
          return;
        }
        updatedUploadedFiles(normalizeResponseHeaders(data?.results || []));
        refreshTableData();
      } catch (err) {
        setInvalidRows([{
          rowNumber: 0,
          errors: [{ field: 'error', message: `Upload failed: ${err.message}` }]
        }]);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <>
        <ButtonStyled appearance='primary' onClick={closeModal} disabled={uploadedFiles?.length < 1}>
          Done
        </ButtonStyled>
        <ButtonStyled
          className={`${classname}__dialog__upload`}
          appearance='primary'
          onClick={submit}
          disabled={uploadedFiles?.length > 0 || isSubmitting || currentFiles?.length < 1}
        >
          Upload
        </ButtonStyled>
        <ButtonStyled appearance='secondary' onClick={closeModal} data-testid='uploadCatalog-close-icon'>
          Close
        </ButtonStyled>
      </>
    );
  };

  return <Modal title={title} content={content} footer={footer} isOpen={isOpen} closeModal={closeModal} />;
};

UploadCatalogModal.propTypes = {
  closeModal: func.isRequired,
  isOpen: bool.isRequired,
  classname: string.isRequired
};

UploadCatalogModal.defaultProps = {};

export default UploadCatalogModal;
